import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useNews, mapApiItemToArticle } from '../context/NewsContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft } from 'react-icons/fi';
import {
  fetchPitchFeedArticle,
  PUBLIC_SITE_FEED_LANG,
} from '../api/pitchFeed';
import {
  escapeHtmlText,
  htmlToPlainText,
  sanitizePublicArticleHtml,
  stripLeadingDuplicateHeadline,
} from '../utils/html';
import {
  articlePathId,
  resolveArticleRouteId,
} from '../utils/articleId';

const Article = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const articleId = useMemo(
    () => resolveArticleRouteId(routeId),
    [routeId],
  );
  const { getArticleById, feedStatus } = useNews();
  /** `idle` until first fetch attempt finishes — avoids false "Brief not found" while feed/API loads. */
  const [detailFetchStatus, setDetailFetchStatus] = useState('idle');
  const [detailHtml, setDetailHtml] = useState('');
  /** Locale the current `detailHtml` belongs to — hide long HTML on mismatch so we show feed stub instantly (no blank flash). */
  const [detailForLocale, setDetailForLocale] = useState(null);
  const [feedItemRaw, setFeedItemRaw] = useState(null);
  const [fallbackArticle, setFallbackArticle] = useState(null);
  const fetchGenRef = useRef(0);

  /** New article route: drop cached `/v1/articles` row until the next fetch completes. */
  useLayoutEffect(() => {
    setFeedItemRaw(null);
    setFallbackArticle(null);
    setDetailHtml('');
    setDetailForLocale(null);
    setDetailFetchStatus('idle');
  }, [articleId]);

  useEffect(() => {
    if (!routeId) return;
    const canonical = articlePathId(routeId);
    if (canonical && canonical !== routeId) {
      navigate(`/article/${encodeURIComponent(canonical)}`, { replace: true });
    }
  }, [routeId, navigate]);

  /** Same-render remap for direct `/article/...` links (no one-frame stale locale from `useState`). */
  const articleFromFetchedItem = useMemo(() => {
    if (!feedItemRaw) return null;
    return mapApiItemToArticle(feedItemRaw);
  }, [feedItemRaw]);

  useEffect(() => {
    if (!articleId) return undefined;
    const gen = ++fetchGenRef.current;
    let cancelled = false;
    setDetailFetchStatus('loading');
    /** Avoid showing long HTML from the previous `?lang=` while the new locale is in flight. */
    setDetailForLocale(null);
    setDetailHtml('');
    (async () => {
      try {
        const data = await fetchPitchFeedArticle(articleId, {
          locale: PUBLIC_SITE_FEED_LANG,
        });
        if (cancelled || gen !== fetchGenRef.current) {
          return;
        }
        if (!data?.item) {
          setDetailFetchStatus('error');
          return;
        }
        setFeedItemRaw(data.item);
        setFallbackArticle(mapApiItemToArticle(data.item));
        setDetailForLocale(PUBLIC_SITE_FEED_LANG);
        if (
          typeof data.detail_body_html === 'string' &&
          data.detail_body_html.trim()
        ) {
          setDetailHtml(data.detail_body_html);
        } else {
          /** Clear so we do not show the previous locale's long HTML as the new locale (`longOk` + `hasLong` would match wrongly). */
          setDetailHtml('');
        }
        setDetailFetchStatus('ok');
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn("[Article] detail fetch failed", e);
        }
        if (!cancelled && gen === fetchGenRef.current) {
          setDetailFetchStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  /** Prefer `/v1/articles` payload over feed cache so list stubs do not mask fresh item + detail. */
  const article =
    articleFromFetchedItem ??
    fallbackArticle ??
    getArticleById(articleId);

  /** Keep spinner if feed is still loading (article may appear when fetch completes) or until detail fetch leaves idle/loading without error. */
  const waitingForArticle =
    Boolean(articleId) &&
    !article &&
    (feedStatus.loading ||
      (detailFetchStatus !== 'error' &&
        (detailFetchStatus === 'loading' || detailFetchStatus === 'idle')));

  if (waitingForArticle) {
    return (
      <div className="min-h-screen bg-[#1e1b4b] flex items-center justify-center pt-20">
        <div className="text-center">
          <p className="text-slate-400 text-sm font-mono-data uppercase tracking-widest mb-4">
            Loading brief…
          </p>
          <div className="h-1 w-48 mx-auto bg-white/10 rounded overflow-hidden">
            <div className="h-full w-1/3 bg-[#38bdf8] animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#1e1b4b] flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-black text-white mb-6">Brief not found</h2>
          <Link
            to="/news"
            className="text-[#fb7185] uppercase text-xs font-black tracking-widest hover:underline"
          >
            Return to feed
          </Link>
        </div>
      </div>
    );
  }

  const titleStr = String(article.title || '').trim();
  const excerptStr = String(article.excerpt || '').trim();
  const hasFeedStub = Boolean((article.bodyHtml || '').trim());
  /** Always non-empty so the column never collapses when switching locale or waiting on `/v1/articles`. */
  const minStubHtml = `<p>${escapeHtmlText(excerptStr || titleStr)}</p>`;
  const feedStubHtml = hasFeedStub ? article.bodyHtml : minStubHtml;
  const longOk = detailForLocale === PUBLIC_SITE_FEED_LANG;
  const hasLong = Boolean(detailHtml && String(detailHtml).trim());
  let rawBodyHtml = '';
  if (longOk && hasLong) {
    rawBodyHtml = detailHtml;
  } else if (hasFeedStub) {
    rawBodyHtml = article.bodyHtml;
  } else if (hasLong) {
    rawBodyHtml = detailHtml;
  } else {
    rawBodyHtml = feedStubHtml;
  }
  const safeBody = sanitizePublicArticleHtml(rawBodyHtml);
  const bodyAfterHeadlineDedup = stripLeadingDuplicateHeadline(
    safeBody,
    titleStr,
  );
  const bodyPlain = htmlToPlainText(bodyAfterHeadlineDedup);
  const titleNorm = titleStr.replace(/\s+/g, ' ').toLowerCase();
  const bodyNorm = bodyPlain.replace(/\s+/g, ' ').toLowerCase();
  const isStubBody =
    !bodyAfterHeadlineDedup ||
    !bodyPlain ||
    bodyNorm === titleNorm ||
    bodyPlain.length <= titleStr.length + 40;
  const excerptAdds =
    excerptStr &&
    excerptStr.replace(/\s+/g, ' ').toLowerCase() !== titleNorm;

  let mainHtml = bodyAfterHeadlineDedup;
  if (isStubBody && excerptAdds) {
    mainHtml = `${bodyAfterHeadlineDedup || `<p>${escapeHtmlText(titleStr)}</p>`}<p>${escapeHtmlText(excerptStr)}</p>`;
  } else if (!mainHtml) {
    mainHtml = `<p>${escapeHtmlText(String(article.content || '').trim() || titleStr)}</p>`;
  }

  mainHtml = stripLeadingDuplicateHeadline(mainHtml, titleStr);

  const excerptEmbeddedInMain = isStubBody && excerptAdds;
  const showDek =
    excerptStr &&
    excerptStr.toLowerCase() !== titleNorm &&
    (!bodyAfterHeadlineDedup || bodyPlain.length < titleStr.length + 120) &&
    !excerptEmbeddedInMain;

  const fallbackParagraphs = String(article.content || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="bg-[#1e1b4b] min-h-screen pt-40 pb-32 px-6">
      <motion.article
        initial={false}
        animate={{ opacity: 1 }}
        className="max-w-4xl mx-auto"
      >
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-[#38bdf8] uppercase text-[10px] font-black tracking-widest mb-12 hover:text-white transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} /> Back to feed
        </Link>

        {article.imageUrl ? (
          <div className="relative aspect-video rounded-[32px] overflow-hidden mb-10 border border-white/10 shadow-2xl">
            <img
              src={article.imageUrl}
              className="w-full h-full object-cover grayscale opacity-60"
              alt=""
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e1b4b] via-transparent to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter leading-none">
                {article.title}
              </h1>
            </div>
          </div>
        ) : (
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter leading-tight mb-10 max-w-3xl">
            {article.title}
          </h1>
        )}

        <div className="max-w-3xl">
          {showDek && (
            <p className="text-2xl font-medium text-slate-300 leading-relaxed italic mb-12">
              {article.excerpt}
            </p>
          )}
          <div
            className="article-body max-w-none text-slate-300 [&_p]:mb-6 [&_p]:text-lg [&_p]:leading-relaxed [&_p:last-child]:mb-0 [&_a]:text-[#38bdf8] [&_a]:underline [&_strong]:font-semibold [&_strong]:text-slate-100 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6"
            dangerouslySetInnerHTML={{ __html: mainHtml }}
          />
          {!htmlToPlainText(mainHtml).trim() &&
            fallbackParagraphs.map((p, i) => (
              <p
                key={i}
                className="text-lg text-slate-400 leading-relaxed mb-8"
              >
                {p}
              </p>
            ))}
        </div>
      </motion.article>
    </div>
  );
};

export default Article;